// re.rejouis.fr — RETOURS APRÈS ACHAT : règles partagées entre l'écran /retour et la
// fonction serveur /api/retour (le serveur recalcule tout, l'écran ne fait qu'afficher).
//
// Décisions Benoni (2026-10-20) :
//   - Défectueux / erreur de commande : RÉCLAMATION — preuve photo ou vidéo (+ solution au
//     choix pour le défectueux, cf. SOLUTIONS), validée à la main dans le lab (renvoi
//     seulement s'il le demande).
//   - Changement d'avis : 14 jours après réception ; 4,80 € retenus pour le transport ;
//     sachet plastique ouvert → 30 € retenus en plus par article, sauf catégories
//     « sans retenue » (livres, accessoires kinky…) ; produits d'hygiène ouverts → refusés.
// Catégorie = metafield custom.budget_ (liste fixe). ⚠️ Certaines valeurs ont un espace
// final (« Anneau pénien ») : comparer sans .trim() sur la valeur Shopify.

export const RETENUE_TRANSPORT = 4.8;
export const RETENUE_OUVERT = 30;
export const JOURS_RETRACTATION = 14;
// Pas de date de livraison remontée par Mondial Relay → réception estimée à l'expédition + 6 j.
export const JOURS_TRANSIT_ESTIME = 6;
// Défectueux / erreur : garantie légale de conformité (2 ans).
export const JOURS_GARANTIE = 730;

export const REGLES_RETOUR = {
  // Remboursés intégralement même ouverts (pas de reconditionnement lourd).
  sansRetenue: new Set(['Accessoire kinky', "Livres pour s'explorer", 'BD érotiques', 'Jeux pour couples', 'Bijoux et accessoires']),
  // Hygiène : changement d'avis accepté seulement si le produit est encore scellé.
  hygiene: new Set(['Lubrifiant et bien être intime', 'Préservatifs', 'Soin du jouet']),
  // Pas un produit physique : jamais retourné par ce parcours.
  exclu: new Set(['Abonnement annuel']),
};

// Défectueux (2026-10-20, inspiré de Back Market) : PAS de renvoi du produit (il serait
// jeté, le transport retour coûterait pour rien). Le client prouve le défaut (photo,
// vidéo) et choisit sa solution ; l'équipe valide la preuve dans la console lab
// (onglet Réclamations) avant toute exécution.
export const PREUVES_MAX = 3;
export const PREUVE_TAILLE_MAX = 100 * 1024 * 1024; // une courte vidéo de téléphone
export const PREUVE_TYPES = /^(image|video)\//;
// Crédit boutique sans date limite, boosté de 10 % par rapport au remboursement.
export const BONUS_CREDIT = 0.1;
export const SOLUTIONS = [
  { id: 'remboursement', label: 'Remboursement', detail: 'Sur ton moyen de paiement.' },
  { id: 'remplacement', label: 'Remplacement', detail: '' },
  { id: 'credit', label: 'Crédit boutique +10 %', detail: 'Sans date limite, utilisable sur tout rejouis.fr.' },
];
export const solutionParId = (id) => SOLUTIONS.find((s) => s.id === id) || null;
// Remplacement : article identique s'il en reste, sinon un équivalent choisi par le client
// (même catégorie, en stock, jusqu'à +10 % du prix payé). Envoyé par une commande à 0 €
// avec le même mode de livraison que la commande d'origine.
export const MARGE_EQUIVALENT = 0.1;
export const EQUIVALENTS_MAX = 3;
// Échange proposé par l'équipe (erreur de commande) : liste plus large, mêmes règles.
export const EQUIVALENTS_MAX_ECHANGE = 12;
// Boxtal : le point relais est choisi après le paiement et n'est pas dans Shopify ; seul le
// titre du mode de livraison dit relais ou domicile (Colissimo).
export const modeLivraison = (titre) => (/relais|point de retrait/i.test(titre || '') ? 'relais' : 'domicile');

export const MOTIFS = [
  { id: 'defectueux', label: 'L’article est défectueux', gratuit: true, noteObligatoire: true, preuve: true, choixSolution: true },
  // Erreur de commande (2026-10-21) : explication + photo de ce qui a été reçu, SANS choix de
  // solution ; l'équipe répond depuis le lab (étiquette de renvoi ou message personnalisé).
  { id: 'erreur', label: 'Il y a une erreur dans ma commande', gratuit: true, noteObligatoire: true, preuve: true, choixSolution: false },
  { id: 'avis', label: 'J’ai changé d’avis', gratuit: false, noteObligatoire: false },
];
export const motifParId = (id) => MOTIFS.find((m) => m.id === id) || null;

// Pas des produits à renvoyer par colis, repérés par leur nom (décision Benoni 2026-10-20) :
// cartes cadeaux, Le PASS (expérience / jouets « - PASS »), abonnements.
const TITRES_EXCLUS = /carte cadeau|\bpass\b|abonnement/i;

/** 'normal' | 'sans_retenue' | 'hygiene' | 'exclu' selon la catégorie custom.budget_ (et le titre). */
export function regleCategorie(categorie, titre = '') {
  const c = categorie || '';
  const cs = c.trim();
  if (REGLES_RETOUR.exclu.has(c) || REGLES_RETOUR.exclu.has(cs) || TITRES_EXCLUS.test(titre || '')) return 'exclu';
  if (REGLES_RETOUR.sansRetenue.has(c) || REGLES_RETOUR.sansRetenue.has(cs)) return 'sans_retenue';
  if (REGLES_RETOUR.hygiene.has(c) || REGLES_RETOUR.hygiene.has(cs)) return 'hygiene';
  return 'normal';
}

const JOUR = 24 * 3600 * 1000;

/** Date de réception : livraison réelle si connue, sinon expédition + transit estimé. */
export function dateReception({ livreeLe, expedieeLe }) {
  if (livreeLe) return new Date(livreeLe);
  if (expedieeLe) return new Date(new Date(expedieeLe).getTime() + JOURS_TRANSIT_ESTIME * JOUR);
  return null;
}

/** Dernier jour pour un changement d'avis (Date) ou null. */
export function finRetractation(dates) {
  const r = dateReception(dates);
  return r ? new Date(r.getTime() + JOURS_RETRACTATION * JOUR) : null;
}

export function motifPossible(motif, dates, maintenant = new Date()) {
  if (motif === 'avis') {
    const fin = finRetractation(dates);
    return !!fin && maintenant <= fin;
  }
  const exp = dates.expedieeLe ? new Date(dates.expedieeLe) : null;
  return !!exp && maintenant.getTime() <= exp.getTime() + JOURS_GARANTIE * JOUR;
}

/**
 * Calcule le remboursement estimé.
 * lignes : [{ prix, quantite, regle, ouvert }] — prix unitaire payé.
 * Retourne { lignes:[{ ...l, retenue, refus }], retenueTransport, total, refus }.
 */
export function calculRetour(motif, lignes) {
  const gratuit = motifParId(motif)?.gratuit === true;
  let refus = null;
  const out = lignes.map((l) => {
    const q = Math.max(1, Number(l.quantite) || 1);
    const prix = Number(l.prix) || 0;
    let retenue = 0;
    let refusLigne = null;
    if (l.regle === 'exclu') refusLigne = 'Cet article ne peut pas être retourné.';
    else if (!gratuit && l.ouvert) {
      if (l.regle === 'hygiene') refusLigne = 'Produit d’hygiène ouvert : il ne peut pas être repris.';
      else if (l.regle !== 'sans_retenue') retenue = Math.min(RETENUE_OUVERT, prix) * q;
    }
    if (refusLigne && !refus) refus = refusLigne;
    return { ...l, quantite: q, retenue, refus: refusLigne };
  });
  const brut = out.reduce((s, l) => s + l.prix * l.quantite, 0);
  const retenueTransport = gratuit ? 0 : RETENUE_TRANSPORT;
  const retenues = out.reduce((s, l) => s + l.retenue, 0) + retenueTransport;
  const total = Math.max(0, Math.round((brut - retenues) * 100) / 100);
  return { lignes: out, retenueTransport, brut, total, refus };
}

const arrondi = (n) => Math.round(n * 100) / 100;

/** Montant de chaque solution d'une réclamation (défectueux) : prix payé, ou +10 % en crédit. */
export function montantsReclamation(lignes) {
  const brut = arrondi(lignes.reduce((s, l) => s + (Number(l.prix) || 0) * Math.max(1, Number(l.quantite) || 1), 0));
  return { remboursement: brut, credit: arrondi(brut * (1 + BONUS_CREDIT)) };
}
